import React, { useEffect, useRef, useState, ChangeEvent } from 'react'
import PizzeriaFilter from '../../components/ordersPage/ordersTable/filters/PizzeriaFilter'
import { Grid } from '@mui/material'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TablePagination from '@mui/material/TablePagination'
import Paper from '@mui/material/Paper'
import { ICustomer } from 'dto/users.dto'
import UsersService from 'dataStore/service/users.service'
import { useSelector } from 'react-redux'
import { RootState } from 'dataStore/state'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import VpnKeyIcon from '@mui/icons-material/VpnKey'
import { useDownloadExcel } from 'react-export-table-to-excel'
import SetCustomerPasswordDialog from 'components/customers/SetCustomerPasswordDialog'

const Customers = () => {
  const [customers, setCustomers] = useState<ICustomer[]>([])
  const [selectedCustomer, setSelectedCustomer] = useState<ICustomer | null>(
    null
  )
  const [total, setTotal] = useState<number>(0)
  const [page, setPage] = useState<number>(0)
  const [rowsPerPage, setRowsPerPage] = useState<number>(20)
  const { filter } = useSelector((state: RootState) => state.orders)
  const { pizzerias } = useSelector((state: RootState) => state.pizzerias)
  const tableRef = useRef(null)
  const pizzeria = pizzerias?.find(
    pizzeriaItem => pizzeriaItem?.id === filter?.pizzeria_id
  )

  useEffect(() => {
    let isCustomerRequestActive = true

    const loadCustomers = async () => {
      const pagination = {
        limit: rowsPerPage,
        offset: page * rowsPerPage,
      }

      try {
        let response: { data: ICustomer[]; total: number }
        if (filter?.pizzeria_id) {
          // Загружаем пользователей по выбранной пиццерии
          response = await UsersService.getUsersByPizzeriaId(
            filter.pizzeria_id,
            pagination
          )
        } else {
          // Если пиццерия не выбрана, загружаем всех пользователей
          response = await UsersService.getAllCustomers(pagination)
        }

        if (!isCustomerRequestActive) {
          return
        }

        setCustomers(Array.isArray(response?.data) ? response?.data : [])
        setTotal(typeof response?.total === 'number' ? response?.total : 0)
      } catch (error: any) {
        console.log({ error })
        if (isCustomerRequestActive) {
          setCustomers([])
          setTotal(0)
        }
      }
    }

    loadCustomers()

    return () => {
      // Не обновляем таблицу ответом от устаревшего запроса.
      isCustomerRequestActive = false
    }
  }, [filter?.pizzeria_id, page, rowsPerPage])

  /**
   * Переключает страницу таблицы клиентов.
   * @param paginationEvent — событие пагинации таблицы
   * @param newPage — номер выбранной страницы
   */
  const handleChangePage = (
    paginationEvent: unknown,
    newPage: number
  ): void => {
    setPage(newPage)
  }

  /**
   * Изменяет количество клиентов на странице.
   * @param changeEvent — событие изменения размера страницы
   */
  const handleChangeRowsPerPage = (
    changeEvent: ChangeEvent<HTMLInputElement>
  ): void => {
    setRowsPerPage(parseInt(changeEvent?.target?.value ?? '20', 10))
    setPage(0)
  }

  const { onDownload } = useDownloadExcel({
    currentTableRef: tableRef?.current,
    filename: pizzeria?.address?.city ? pizzeria?.address?.city : 'Users',
    sheet: 'Users',
  })

  /**
   * Открывает диалог управления паролем.
   * @param customer — клиент, для которого изменяется пароль
   */
  const handleOpenPasswordDialog = (customer: ICustomer): void => {
    setSelectedCustomer(customer)
  }

  const handleClosePasswordDialog = (): void => {
    setSelectedCustomer(null)
  }

  return (
    <main>
      <Grid container spacing={2} sx={{ mb: '25px' }}>
        <Grid item xs={12} sm={12} md={3}>
          <PizzeriaFilter />
          <Button
            variant='contained'
            fullWidth
            sx={{ mt: 2 }}
            disabled={!customers?.length}
            onClick={onDownload}
          >
            Скачать
          </Button>
        </Grid>
        <Grid item xs={12} sm={12} md={9}>
          <TableContainer component={Paper} sx={{ overflowX: 'auto', maxWidth: '100%' }}>
            <Table size='small' aria-label='a dense table' ref={tableRef} sx={{ minWidth: 480 }}>
              <TableHead>
                <TableRow>
                  <TableCell>N#</TableCell>
                  <TableCell align='right'>id</TableCell>
                  <TableCell align='right'>Имя</TableCell>
                  <TableCell align='right'>Тел.</TableCell>
                  <TableCell align='right'>email</TableCell>
                  <TableCell align='right'>Пароль</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {customers?.map((customer, customerIndex) => (
                  <TableRow
                    key={customer?.id}
                    sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                  >
                    <TableCell component='th' scope='row'>
                      {page * rowsPerPage + customerIndex + 1}
                    </TableCell>
                    <TableCell align='right'>{customer?.id}</TableCell>
                    <TableCell align='right'>{customer?.first_name}</TableCell>
                    <TableCell align='right'>{customer?.phone}</TableCell>
                    <TableCell align='right'>{customer?.email}</TableCell>
                    <TableCell align='right'>
                      <IconButton
                        aria-label={`Управлять паролем клиента ${
                          customer?.first_name || customer?.id
                        }`}
                        onClick={() => handleOpenPasswordDialog(customer)}
                      >
                        <VpnKeyIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[10, 20, 30, 50]}
            component='div'
            count={total}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage='Строк на странице:'
            labelDisplayedRows={({ from, to, count }) =>
              `${from}–${to} из ${count !== -1 ? count : `более чем ${to}`}`
            }
          />
        </Grid>
      </Grid>
      <SetCustomerPasswordDialog
        open={Boolean(selectedCustomer)}
        customer={selectedCustomer}
        onClose={handleClosePasswordDialog}
      />
    </main>
  )
}

export default Customers
